import { scopesAvailable } from "./mapScopes";

// Wording, colours and shapes for the MeshMapper coverage layer (issue #32). Pure, no imports; covered by
// tests/meshmapperCoverage.test.mjs. The data comes from GET /api/meshmapper/coverage (the stored response); nothing here
// calls MeshMapper.

export const MM_LAYER_TITLE = "MeshMapper coverage";
export const MM_SETTINGS_PATH_TEXT = "Settings, MeshMapper";

export interface CoverageMeta {
  enabled: boolean;
  has_data: boolean;
  message?: string;
  region?: string | null;
  region_name?: string | null;
  generated_at?: number | null;
  /** ok, failed (the latest refresh failed) or paused (waiting for the daily limit to reset). */
  refresh_status?: string;
  /** The fill and border colour MeshMapper sends for each coverage type. */
  type_colours?: Record<string, { fill?: string; border?: string }>;
  data_version?: string;
}

/** The painted coverage: one row per hex, [q, r, coverage type] (see `fields`), on the 300 m hex grid at ref_lat. */
export interface CoverageFull extends CoverageMeta {
  hex_size_m: number;
  ref_lat: number;
  fields: string[];
  hexes: unknown[][];
}

// --- time ---------------------------------------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "6:05 PM", in the viewer's own time. */
export function timeOfDayText(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** The one date format used on the map card and in Settings: "Oct 20, 2026, 10:25 AM", in the viewer's own time. */
export function dateTimeText(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${timeOfDayText(d)}`;
}

/** The same format from an ISO time such as "2026-10-20T16:25:45Z"; the text itself if it cannot be read. */
export function isoDateTimeText(iso: string | null | undefined): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? iso : dateTimeText(t / 1000);
}

/** "just now", "12 min ago", "5 h ago", "1 day ago", "40 days ago". */
export function agoText(unixSeconds: number | null | undefined, nowMs: number): string | null {
  if (typeof unixSeconds !== "number" || !(unixSeconds > 0)) return null;
  const secs = Math.max(0, Math.round(nowMs / 1000 - unixSeconds));
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h ago`;
  const d = Math.round(secs / 86400);
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

/** The next midnight UTC in the viewer's own time: "midnight UTC (6:00 PM your time)". */
export function utcMidnightText(nowMs: number): string {
  const now = new Date(nowMs);
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  return `midnight UTC (${timeOfDayText(next)} your time)`;
}

/** The legend's last line. After a failed refresh it says the data is old. */
export function footerLine(
  meta: { region_name?: string | null; region?: string | null; generated_at?: number | null; refresh_status?: string },
  nowMs: number,
): string {
  const name = meta.region_name ?? meta.region;
  const head = `MeshMapper${name ? `, ${name}` : ""}`;
  const ago = agoText(meta.generated_at, nowMs);
  if (!ago) return head;
  if (meta.refresh_status === "failed") return `${head}, last updated ${ago}, latest refresh failed`;
  if (meta.refresh_status === "paused") return `${head}, last updated ${ago}, refreshes paused until the daily limit resets`;
  return `${head}, updated ${ago}`;
}

export const WARDRIVING_LINE = "Wardriving coverage reported by MeshMapper users. Neighbouring hexes of the same type are drawn as one area.";

// --- coverage types and MeshMapper's colours -------------------------------------------------------------------------

export type Theme = "light" | "dark";

export interface TypeColours {
  fill: string;
  border: string;
}

/** MeshMapper's coverage types, in the order its wiki lists them (best first), with the names and wording MeshMapper uses.
 * Sources, all in MeshMapper_Wiki: docs/layers.md ("Ping Types": the names, DISC is shown as DISC/TRACE), docs/visuals.md
 * ("The Grid System" priority order and "Ping Types Defined") and docs/coverage-api.md (the dominant-type table, whose
 * short descriptions are used here). */
export const COVERAGE_TYPES: readonly { type: string; name: string; text: string }[] = [
  { type: "BIDIR", name: "BIDIR", text: "Two-way confirmed link" },
  { type: "DISC", name: "DISC/TRACE", text: "Discovery or trace packet" },
  { type: "TX", name: "TX", text: "Transmitted but not heard back" },
  { type: "RX", name: "RX", text: "Heard traffic, without transmitting" },
  { type: "DEAD", name: "DEAD", text: "Repeater heard but no route" },
  { type: "DROP", name: "DROP", text: "No connection" },
];

/** Used only for a type whose squares carry no colour. The colours the map draws are the ones MeshMapper sends with the
 * data (type_colours); these are the values MeshMapper's wiki lists for each type. */
export const FALLBACK_COLOURS: Record<string, TypeColours> = {
  BIDIR: { fill: "#1e7e34", border: "#14522d" },
  DROP: { fill: "#bd2130", border: "#8b101b" },
  DISC: { fill: "#17a2b8", border: "#117a8b" },
  RX: { fill: "#6f42c1", border: "#59359a" },
  TX: { fill: "#fd7e14", border: "#d96b0c" },
  DEAD: { fill: "#6c757d", border: "#545b62" },
};

/** A colour for a type that is in neither the data nor the fallback table. */
export const UNKNOWN_TYPE_COLOURS: TypeColours = { fill: "#6c757d", border: "#545b62" };

const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** The colours of one type: MeshMapper's own when the data has them, else the fallback table. */
export function coloursFor(type: string, sent: Record<string, { fill?: string; border?: string }> | null | undefined): TypeColours {
  const fb = FALLBACK_COLOURS[type] ?? UNKNOWN_TYPE_COLOURS;
  const got = sent?.[type];
  return {
    fill: got?.fill && HEX_COLOUR.test(got.fill) ? got.fill : fb.fill,
    border: got?.border && HEX_COLOUR.test(got.border) ? got.border : fb.border,
  };
}

/** The legend name of a type: "BIDIR: Two-way confirmed link". */
export function typeLabel(type: string): string {
  const t = COVERAGE_TYPES.find((c) => c.type === type);
  return t ? `${t.name}: ${t.text}` : type;
}

/** Every type the map can show: the six listed, then any other type the data carries. */
export function typesToShow(sent: Record<string, unknown> | null | undefined): string[] {
  const listed = COVERAGE_TYPES.map((c) => c.type);
  return [...listed, ...Object.keys(sent ?? {}).filter((t) => !listed.includes(t))];
}

/** Fill opacity: low enough that the node dots stay readable. MeshMapper's colours are saturated, so this is lower than
 * the pale colours used before; a little higher on the dark map, where the tiles are dark. */
export function fillOpacity(theme: Theme): number {
  return theme === "light" ? 0.4 : 0.45;
}

/** A map-style expression that picks a colour from the feature's `type` property. */
export function typeExpression(which: "fill" | "border", sent: Record<string, { fill?: string; border?: string }> | null | undefined): unknown[] {
  const expr: unknown[] = ["match", ["get", "type"]];
  for (const t of typesToShow(sent)) {
    expr.push(t, coloursFor(t, sent)[which]);
  }
  expr.push(UNKNOWN_TYPE_COLOURS[which]);
  return expr;
}

/** The outline is drawn from this zoom up; below it the merged areas have no outline. */
export const OUTLINE_MIN_ZOOM = 11;

export const ZOOM_IN_MESSAGE = "Zoom in to see coverage";
export const MESHCORE_ONLY_MESSAGE = "MeshMapper is MeshCore only";

/** The legend's link to where the details are: the map only paints coverage. Opens in a new tab. */
export const MM_SOURCE_LINK = { text: "See details on MeshMapper", href: "https://meshmapper.net" } as const;

/** Whether coverage hexes are drawn at all: the layer is on in Settings, this viewer has it on, there is data, and the
 * protocol filter is not Meshtastic only. The protocol rule is the one the scope shading uses (scopesAvailable). */
export function coverageDrawn(o: { available: boolean; layerOn: boolean; hasSquares: boolean; protocolFilter: string }): boolean {
  return o.available && o.layerOn && o.hasSquares && scopesAvailable(o.protocolFilter);
}

// --- Settings block ----------------------------------------------------------------------------------------------------

export const MM_SETTINGS_INTRO = "Uses MeshMapper's Coverage API. Self-service keys allow 100 requests a day.";
export const MM_REFRESH_MIN = 15;
export const MM_REFRESH_MAX = 240;

/** What GET /api/meshmapper/status returns. */
export interface MeshMapperStatus {
  enabled: boolean;
  refresh_minutes: number;
  key_set: boolean;
  state: string;
  last_attempt_at: string | null;
  last_fetch_at: string | null;
  last_result: string | null;
  last_error: string | null;
  region: string | null;
  region_name: string | null;
  squares: number;
  requests_today: number;
  daily_cap: number;
  next_poll_at: string | null;
}

/** Where the poll loop is, in a few words. */
export function stateText(state: string): string {
  switch (state) {
    case "off":
      return "Switched off";
    case "no_key":
      return "No key set";
    case "blocked":
      return "Stopped: MeshMapper did not accept the key";
    case "paused":
      return "Paused until the daily limit resets";
    case "backoff":
      return "Waiting before trying again";
    case "waiting":
      return "Waiting for the next refresh";
    case "due":
      return "Fetching now";
    default:
      return state;
  }
}

/** The result of the last request, in a few words. */
export function resultText(result: string | null | undefined): string {
  switch (result) {
    case "updated":
      return "Updated";
    case "unchanged":
      return "Checked, nothing new";
    case "backoff":
      return "Asked to wait, will try again";
    case "daily_limit":
    case "paused":
      return "Daily limit reached";
    case "rejected":
      return "Key not accepted";
    case "error":
      return "Failed";
    default:
      return "Not fetched yet";
  }
}

/** The refresh interval as typed: a whole number from 15 to 240, else null. */
export function parseRefreshMinutes(text: string): number | null {
  if (!/^\s*\d+\s*$/.test(text)) return null;
  const n = parseInt(text, 10);
  return n >= MM_REFRESH_MIN && n <= MM_REFRESH_MAX ? n : null;
}

/** Fetches per day at a refresh time, as the daily limit sees it. */
export function requestsPerDay(minutes: number): number {
  return Math.ceil(1440 / minutes);
}

/** The Settings note shown when the refresh time is low enough to reach the daily limit; null otherwise. */
export function capNote(minutes: number, dailyCap: number, nowMs: number): string | null {
  if (!(minutes > 0) || requestsPerDay(minutes) <= dailyCap) return null;
  return `Every ${minutes} minutes needs ${requestsPerDay(minutes)} requests a day, more than the limit of ${dailyCap}. Fetches will stop for the day once the limit is reached and start again after ${utcMidnightText(nowMs)}.`;
}

/** How a legend's source link opens: in a new tab, without handing the new page this one. */
export const SOURCE_LINK_REL = "noopener noreferrer";
